// Typed WebSocket client for /sessions/:id/stream.
//
// Mirrors the wire format in apps/server/src/ws/serialize.ts:
//
//   server → client    { kind: 'event',   payload: AgentEvent }
//                      { kind: 'control', payload: ServerControl }
//
//   client → server    { kind: 'user_message', payload: { text } }
//                      { kind: 'ping' }
//
// The hook in chat/useWsSession.ts is the consumer; this module is the
// schema + low-level send / reconnect plumbing.

import { z } from "zod";
import type { AgentEvent } from "@loom/shared";

import { wsDiag } from "./ws-diag";

const gateBlockHintsSchema = z.object({
  kind: z.string(),
  missing: z.array(z.string()).optional(),
  verdicts: z
    .array(
      z.object({
        criterion: z.string(),
        level: z.enum(["ok", "warn", "fail"]),
        evidence: z.string(),
        suggestion: z.string().nullable(),
      }),
    )
    .optional(),
  extra: z.record(z.unknown()).optional(),
});

const lifecycleOutcomeSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("continue") }),
  z.object({ kind: z.literal("augment"), systemNote: z.string() }),
  z.object({
    kind: z.literal("block"),
    reason: z.string(),
    hints: gateBlockHintsSchema.optional(),
  }),
]);

const envelopeFields = {
  sessionId: z.string(),
  seq: z.number().int(),
  emittedAt: z.string(),
  // Post-green tail-turn stamp: era-tagged stream/prose events narrate over
  // repo paths and MUST NOT render as chat content (see useWsSession).
  era: z.literal("tail").optional().catch(undefined),
};

const agentEventSchemaRaw = z.discriminatedUnion("type", [
  z.object({ ...envelopeFields, type: z.literal("assistant_delta"), text: z.string() }),
  z.object({
    ...envelopeFields,
    type: z.literal("tool_use_start"),
    toolUseId: z.string(),
    toolName: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("tool_use_input"),
    toolUseId: z.string(),
    input: z.unknown(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("tool_use_output"),
    toolUseId: z.string(),
    output: z.unknown(),
    ok: z.boolean(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("lifecycle_decision"),
    phase: z.enum(["userPromptSubmitted", "beforeToolCall", "afterToolCall", "sessionEnded"]),
    handler: z.string(),
    outcome: lifecycleOutcomeSchema,
    toolUseId: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("role_spawn"),
    toolUseId: z.string(),
    role: z.string(),
    task: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("role_return"),
    toolUseId: z.string(),
    role: z.string(),
    summary: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("error"),
    message: z.string(),
    terminal: z.boolean(),
    code: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("reauth_required"),
    gitlabHost: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("budget_exhausted"),
    kind: z.enum([
      "tokens",
      "wall_time",
      "spend",
      "executor_cpu",
      "executor_wall",
    ]),
    limit: z.number(),
    used: z.number(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("tools_unavailable"),
    reason: z.enum(["refused", "unreachable"]),
    detail: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("question_pending"),
    issueIid: z.number(),
    origin: z.enum(["servant", "sandbox"]),
    asks: z.array(
      z.object({
        askId: z.string(),
        field: z.string().nullable(),
        question: z.string(),
        help: z.string().optional(),
        optional: z.boolean().optional(),
        // A CONCERN (`"concern"`) is the agent flagging a problem WITH something
        // already settled, rather than asking for something missing. Deliberately
        // `z.string()`, NOT `z.enum` — a `safeParse` failure hands the error to
        // `onProtocolError` and RETURNS, discarding the whole frame and every ask
        // in it, so a future third kind would silently cost the user their
        // questions. Unknown values normalise away in the fold (→ plain question).
        kind: z.string().optional(),
        // Loom's draft answer rows for this ask (see the readiness verdict's
        // `draft` below). Declare-or-it-is-stripped, and `.catch(undefined)`
        // for the same reason as `kind` is a bare string: a malformed draft
        // must cost the pre-fill, never the questions in the frame.
        draft: z.array(z.string()).optional().catch(undefined),
        // WHAT this ask is about, when it is about a Loom-owned artefact
        // rather than a value to type. The only member today is the Figma
        // sketch — `{ kind: "figma_preview", previewId }` — and its presence is
        // what turns a `field: null` ask into the preview step rather than a
        // free text question. The ask's body is a DRAWING the surface fetches
        // with `previewId`; the drawing itself never rides the bus.
        //
        // Declare-or-it-is-stripped, and this one is stripped at THREE seams,
        // not one: `question-event.ts` builds each ask key by key server-side,
        // this mirror parses it, and `useWsSession`'s fold rebuilds the entry
        // key by key again. All three must name it (the `widget` / `draft` /
        // `gateRefusal` lesson, three times over).
        //
        // `kind` is a bare `z.string()` for the same reason the ask's own
        // `kind` is: a `safeParse` failure discards the WHOLE frame and every
        // question in it, so a subject word from a newer server must cost the
        // rendering of one ask, never the batch. Unknown subjects normalise
        // away in the fold, and a subject missing its handle fails this object
        // and lands on the `.catch(undefined)` below — a plain question, which
        // answers nothing but is never a broken screen.
        //
        // `previewId` is REQUIRED and `linkId` is not, mirroring
        // `FigmaPreviewAskSubject` exactly: the handle addresses the read, the
        // link id is a display-only alias a whole-file chip does not carry.
        subject: z
          .object({
            kind: z.string(),
            previewId: z.string(),
            linkId: z.string().optional(),
          })
          .optional()
          .catch(undefined),
      }),
    ),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("question_resolved"),
    issueIid: z.number(),
    askIds: z.array(z.string()),
    reason: z.enum(["answered", "declined", "carded", "superseded"]),
    answeredAtSeq: z.number().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("bridge_status"),
    state: z.enum(["pending", "cloning", "ready", "terminated", "error"]),
    pipelineId: z.number().int().optional(),
    etaSeconds: z.number().int().nonnegative().optional(),
    message: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("sandbox_status"),
    phase: z.enum([
      "idle",
      "triggering",
      "waiting-for-runner",
      "booting",
      "ready",
      "degraded",
      "suspended",
      "terminated",
    ]),
    pipelineId: z.number().int().optional(),
    message: z.string().optional(),
    // When the phase began (ISO, the server's clock). Optional: an older server
    // omits it and the page falls back to the frame's arrival.
    since: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("bridge_stream"),
    channel: z.enum(["stdout", "stderr"]).optional(),
    chunk: z.string().optional(),
    payload: z.unknown().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("bridge_rpc"),
    correlationId: z.string(),
    ok: z.boolean(),
    payload: z.unknown(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("mode_changed"),
    from: z.enum(["pm", "coding"]),
    to: z.enum(["pm", "coding"]),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("coding_branch_state"),
    commits: z.number().int(),
    summary: z.string().nullable(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("agent_turn"),
    state: z.enum(["busy", "idle"]),
    // `busy` only: when this busy stretch began (ISO, the server's clock).
    since: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("coding_progress"),
    entry: z.unknown(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("proposal_pending"),
    proposalId: z.string(),
    proposal: z.unknown(),
    toolUseId: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("proposal_confirmed"),
    proposalId: z.string(),
    outcome: z.unknown(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("proposal_rejected"),
    proposalId: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("user_message"),
    text: z.string(),
    // Delivery id the echo carries when the client sent one (must be declared
    // or Zod strips it — the silent-drop hazard the readiness comment below
    // documents). Not consumed by the timeline; the ingress `ack` control is
    // the delivery signal.
    clientMsgId: z.string().optional(),
    // Flow identity (Phase 1 — flow boundaries), stamped server-side and
    // replayed on reconnect. MUST be declared here or Zod strips it on replay —
    // the `_noMissingEventTypes` guard below only checks discriminants, not
    // fields, so a missing field is a silent drop with no compile error.
    issueIid: z.number().int().optional(),
    boundary: z.literal(true).optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_card_pending"),
    issueIid: z.number().int(),
    verdicts: z.array(
      z.object({
        field: z.string(),
        verdict: z.enum(["green", "red", "needs_input", "unavailable"]),
        reason: z.string(),
        resolvedSource: z.enum(["user", "code", "meeting"]).nullable(),
        // The model-derived value of a `proposed` slot, echoed for display so the
        // Focus surface can render a "Loom set X — keep/change?" review. Like
        // `widget`, it MUST be declared here or Zod strips it on the wire (the
        // silent-drop hazard this comment block warns about). Not load-bearing
        // for the gate (allGreen/acceptToken are separate top-level fields).
        value: z.string().optional(),
        // Mirror of `@loom/shared` widgetSpecSchema (this validator hand-copies
        // the verdict shape; Zod strips unknown keys, so a forgotten field is a
        // SILENT drop). `.catch(undefined)` is deliberate: an unrecognised
        // widget variant degrades to "no widget" rather than failing the whole
        // discriminatedUnion and dropping the entire readiness card.
        widget: z
          .discriminatedUnion("type", [
            z.object({ type: z.literal("text") }),
            z.object({ type: z.literal("list_builder") }),
            z.object({
              type: z.literal("single_select"),
              options: z.array(
                z.object({ value: z.string(), label: z.string() }),
              ),
            }),
            z.object({
              type: z.literal("dynamic_select"),
              source: z.literal("milestone"),
            }),
          ])
          .optional()
          .catch(undefined),
        // Loom's draft answer rows for an OPEN question (`@loom/shared`
        // draftSchema). Same declare-or-it-is-stripped rule as `widget`, and
        // the same `.catch(undefined)` degradation: a malformed draft costs
        // the pre-fill, never the card. Display-only — the rows are not a
        // value until the user sends them.
        draft: z.array(z.string()).optional().catch(undefined),
        // This non-green verdict is the SERVER refusing the model, not the agent
        // asking the user — so `reason` is written for the model and no surface
        // may render it. Same declare-or-it-is-stripped rule as `widget`/`draft`,
        // and the cost of forgetting it is the whole defect coming back: the
        // client would see a bare `needs_input`, treat the gate's own repair
        // instruction as the question text, and put it in front of a user who
        // already answered.
        gateRefusal: z.boolean().optional().catch(undefined),
        // The model rewrote this green over a value the user had grounded, with
        // no new user turn behind it. Drives the ready-preview note that is the
        // only place the user is told; stripped here, the rewritten value would
        // go to GitLab under "from what you told Loom" with nothing said.
        revised: z.boolean().optional().catch(undefined),
        // The user pressed Skip on this optional field. `projectCardOutput`
        // stamps it onto the slot, and Focus folds it into `declinedAll` — the
        // DURABLE half of the skip mask, the mount-local half being empty on
        // every remount. Same declare-or-it-is-stripped rule as the three
        // above, and it is where that rule was broken: the flag was declared on
        // the `question_pending` ask object instead (both objects have a
        // `draft:` line), so nothing on the wire carried it and a skipped
        // question came back on the next reload — the exact defect the durable
        // skip exists to close. `.catch(undefined)` for the local reason, not
        // shared's bare `.optional()`: a malformed flag must cost the mask, not
        // fail the discriminatedUnion member and drop the whole card.
        declined: z.boolean().optional().catch(undefined),
        // A `meeting` green from an answer given on the meeting page or its
        // link (refine light, `@loom/shared` meetingAnswerSchema). It counts
        // as given: Focus shows no "keep it?" review for it, only its
        // provenance on the ready preview. Declared or stripped, like the
        // rest; a malformed one costs the note, never the card.
        meetingAnswer: z
          .object({ via: z.enum(["owner", "link"]), answeredAt: z.string() })
          .optional()
          .catch(undefined),
      }),
    ),
    dor: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
    dod: z.object({ ok: z.boolean(), missing: z.array(z.string()) }),
    invest: z.object({
      verdicts: z.array(
        z.object({
          criterion: z.string(),
          level: z.enum(["ok", "warn", "fail"]),
          evidence: z.string(),
          suggestion: z.string().nullable(),
        }),
      ),
      score: z.number().int(),
    }),
    methodologySkipped: z.boolean(),
    allGreen: z.boolean(),
    acceptToken: z.string().optional(),
    // Highest event-log seq the card's grounding scan had visible. Drives the
    // seq-keyed flush suppression in RefineFlow: a card stamped below the
    // acked flush seq provably predates the answer and must not re-ask the
    // flushed fields. Optional — absent from old servers / unstamped rows.
    groundedThroughSeq: z.number().int().optional(),
    // In-flight ready-screen field edits the agent hasn't folded back into the
    // card yet. The ready surface renders these fields amber and disarms Accept
    // until they clear. MUST be declared or Zod strips it on the wire (the
    // silent-drop hazard this file's :210 warning documents). Optional for
    // deploy-skew — an old server omits it.
    pendingEdits: z.array(z.object({ field: z.string() })).optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_apply_result"),
    issueIid: z.number().int(),
    ok: z.boolean(),
    webUrl: z.string().optional(),
    error: z.string().optional(),
    // A failure's closed reason (`APPLY_ERROR_CODES`); Focus renders copy for
    // it, never `error`. Declared or zod strips it. Optional: an older server
    // sends none, and the client reads the head of `error` instead.
    code: z.string().optional(),
    // Present only when the write left out the implementation-details section.
    // MUST be declared here or zod strips it silently (this file's :210
    // warning) — the same drop that killed `declined` and `gateRefusal`.
    // Optional in both directions for deploy skew.
    planSectionWritten: z.boolean().optional(),
    planDropReason: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_plan_ready"),
    issueIid: z.number().int(),
    // Only the non-technical summary crosses the wire — no file paths (they stay
    // server-side on CardState), so the frame can't leak the implementation.
    businessSummary: z.string(),
    // Which REVISION of the persisted plan this summary renders (the plan's
    // `updatedAtMs`). The confirm frame echoes it back so a "yes, that matches"
    // can only ever confirm the text the user was actually shown: the tail turn
    // asks for plan + sizing together and can land a newer plan while the
    // validate screen is up, and this client drops that newer frame (the
    // consumer effect only acts in `plan_loading`). Optional for deploy skew —
    // an older server echoes none, and a confirm is then withheld entirely.
    planRevision: z.number().int().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_plan_unavailable"),
    issueIid: z.number().int(),
    reason: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_sizing_ready"),
    issueIid: z.number().int(),
    // Path-free by construction: aggregates + buckets only.
    sizeSummary: z.string(),
    overall: z.object({
      filesTouched: z.number().int(),
      locBucket: z.enum(["XS", "S", "M", "L", "XL"]),
      confidence: z.enum(["low", "medium", "high"]),
    }),
    risks: z.array(
      z.enum([
        "db_migration",
        "api_contract_change",
        "auth_or_permissions",
        "data_backfill",
        "cross_service",
        "ui_and_backend",
        "external_integration",
        "performance_sensitive",
        "i18n",
      ]),
    ),
    criteriaCount: z.number().int(),
    splitRecommended: z.boolean(),
    // Big, but only one criterion → offer "refine into criteria", not a split.
    // Optional: an older server omits it.
    oversizedSingleCriterion: z.boolean().optional(),
    // `splitRecommended` is a verdict about criteria that have since MOVED (the
    // user parked or restored one at the split step). Read it as "we don't
    // know" and ASK — never as a decision. Optional: an older server omits it,
    // which is exactly the pre-flag behaviour.
    verdictStale: z.boolean().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_sizing_unavailable"),
    issueIid: z.number().int(),
    reason: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_breakdown_ready"),
    issueIid: z.number().int(),
    noSplit: z.boolean(),
    rationale: z.string(),
    criteria: z.array(z.string()),
    children: z.array(
      z.object({
        localId: z.string(),
        /** Server-minted stable id — what a typed op addresses. Absent on a
         *  partition proposed before ids existed; the card then falls back to
         *  the prose lane rather than sending unaddressable ops. */
        childId: z.string().optional(),
        title: z.string(),
        summary: z.string(),
        /** The model's "how to build it" note — the ONE model-authored byte in
         *  the sub-issue body that is not already on the parent, which is why it
         *  is on the card at all: Confirm authorises it. Path-free server-side
         *  (the file list never leaves the server). Absent on an older card. */
        approach: z.string().optional(),
        coveredCriteria: z.array(z.number().int()),
        estimate: z.object({
          filesTouched: z.number().int(),
          locBucket: z.enum(["XS", "S", "M", "L", "XL"]),
        }),
      }),
    ),
    /** Verbatim must-haves the user took out of scope at the split step. */
    parked: z.array(z.string()).optional(),
    breakdownToken: z.string().optional(),
    /** What the token authorises — sent back as `baseChildrenHash` on every
     *  typed op so an action composed against a superseded card is refused
     *  rather than silently applied to a different partition. */
    childrenHash: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_breakdown_op_result"),
    issueIid: z.number().int(),
    ok: z.boolean(),
    error: z.string().optional(),
    message: z.string().optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_breakdown_unavailable"),
    issueIid: z.number().int(),
    reason: z.string(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_breakdown_progress"),
    issueIid: z.number().int(),
    localId: z.string(),
    step: z.enum(["created", "linked"]),
    childIid: z.number().int(),
    childTitle: z.string(),
    index: z.number().int(),
    total: z.number().int(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("readiness_breakdown_result"),
    issueIid: z.number().int(),
    ok: z.boolean(),
    children: z
      .array(
        z.object({
          localId: z.string(),
          iid: z.number().int(),
          title: z.string(),
          url: z.string().optional(),
        }),
      )
      .optional(),
    error: z.string().optional(),
    progress: z
      .object({
        created: z.number().int(),
        linked: z.number().int(),
        total: z.number().int(),
      })
      .optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("implement_stage"),
    issueIid: z.number().int(),
    stage: z.enum([
      "boot",
      "understand",
      "implement",
      "verify",
      "mr",
      "deploy",
      "done",
      "failed",
    ]),
    status: z.enum(["active", "done", "failed"]),
    detail: z.string().optional(),
    mrUrl: z.string().optional(),
    environments: z
      .array(z.object({ name: z.string(), url: z.string() }))
      .optional(),
    dorBlocked: z
      .object({
        missing: z.array(z.string()),
        haltKind: z.literal("epic").optional(),
        childIids: z.array(z.number().int()).optional(),
      })
      .optional(),
  }),
  z.object({
    ...envelopeFields,
    type: z.literal("figma_link_state"),
    links: z.array(
      z.object({
        linkId: z.string(),
        // `state` and `kind` are deliberately `z.string()`, NOT `z.enum` — the
        // same rule `question_pending.kind` states above. A `safeParse` failure
        // hands the error to `onProtocolError` and RETURNS, discarding the whole
        // frame, so a newer server's thirteenth state would cost the user every
        // chip in the batch instead of one badge. Normalised in `useWsSession`'s
        // fold against the closed `FigmaLinkState` / `FigmaLinkKind` unions.
        state: z.string(),
        kind: z.string().optional(),
        // Declare-or-it-is-stripped: zod drops unknown keys, so a field the
        // server sends and this mirror forgets evaporates with no type error and
        // no test failure (the `gateRefusal` / `draft` lesson). `.catch` on the
        // shaped ones so a malformed count costs the count, never the row.
        label: z.string().optional(),
        folderName: z.string().optional(),
        frameName: z.string().optional(),
        lastModifiedIso: z.string().optional(),
        fileVersion: z.string().optional(),
        counts: z
          .object({
            nodes: z.number().int().optional(),
            frames: z.number().int().optional(),
            textNodes: z.number().int().optional(),
          })
          .optional()
          .catch(undefined),
        commentCount: z.number().int().optional().catch(undefined),
        retryAfterS: z.number().optional().catch(undefined),
        retryAtIso: z.string().optional().catch(undefined),
        declineScope: z.enum(["link", "session"]).optional().catch(undefined),
        // `.catch`, not a bare enum: a refusal word from a newer server costs
        // the refusal line (the button comes back, and the server's throttle
        // holds), never the row.
        allowRefusal: z
          .enum([
            "no_allowlist",
            "access_denied",
            "write_failed",
            "concurrent_write",
            "precondition_unavailable",
            "unavailable",
            "unknown_link",
          ])
          .optional()
          .catch(undefined),
        allowed: z.literal(true).optional().catch(undefined),
        inMessage: z
          .object({
            index: z.number().int(),
            count: z.number().int(),
            skipped: z.number().int().optional().catch(undefined),
          })
          .optional()
          .catch(undefined),
        // `state` a bare string for the reason the row's own is: a refusal word
        // from a newer server costs the line its reason, never the row.
        refreshRefusal: z
          .object({
            state: z.string(),
            retryAtIso: z.string().optional().catch(undefined),
          })
          .optional()
          .catch(undefined),
        // The echo seq of the message that carried the link. `.catch` so a
        // malformed seq costs the placement (the chip falls back to the foot
        // of the transcript), never the row.
        sourceSeq: z.number().int().optional().catch(undefined),
        // The digest came from Loom's own copy, not from a Figma call in this
        // turn. Declare-or-it-is-stripped, like every field above it: a server
        // that sends this and a mirror that forgets it would silently render a
        // stale copy as a fresh read, with no type error and no failing test.
        // `.catch` so a malformed stamp costs the line, never the row.
        fromStore: z
          .object({ readAtIso: z.string() })
          .optional()
          .catch(undefined),
      }),
    ),
  }),
  // The drawing of the Figma frame this chat read, kept in the chat history so
  // a restored conversation shows the picture it actually had (the design store
  // keeps one row per frame, so a re-fetch draws today's copy instead).
  //
  // Declared HERE so the frame PARSES. A variant this union forgets fails the
  // whole-frame `safeParse`, which hands `onProtocolError` an error and prints
  // a red "protocol error" row in the timeline — on every reconnect replay, for
  // every scene, until the tab reloads. That is the cost of a missing line, and
  // it is why this line ships in its own deploy ahead of the server emit.
  //
  // `scene` is `z.unknown()` on purpose, and this is the one variant where the
  // mirror does not restate the shape. The draw list is re-parsed in the FOLD
  // (`chat/useWsSession.ts`) against `lib/design-scene.ts`'s permissive mirror,
  // so a draw list from a newer projector costs THE ENTRY — and falls back to
  // the GET, which re-projects — instead of the whole frame. Validating it here
  // would make a malformed drawing cost the frame instead of the picture, the
  // opposite of what this union's `.catch`es are for.
  //
  // The payload carries no file key, no node id and no layer name —
  // positions, sizes, colours and the visible words only. The handles are
  // sha256 slices.
  z.object({
    ...envelopeFields,
    type: z.literal("figma_scene"),
    previewId: z.string(),
    // Keyed in the fold by `previewId` AND by every `linkId` the read was
    // about. `.catch([])` so a malformed list costs the ids, not the frame.
    linkIds: z.array(z.string()).catch([]),
    scene: z.unknown(),
  }),
]);

export const agentEventSchema = agentEventSchemaRaw as unknown as z.ZodType<AgentEvent>;

// Compile-time guard against the exact drift that produced the
// "invalid_union_discriminator … user_message" crash in prod AND the
// readiness_card_pending drop: an `AgentEvent` variant added in
// `@loom/shared` that this Zod schema forgot to mirror. It checks DISCRIMINANT
// coverage against the UN-annotated `agentEventSchemaRaw` — `z.infer` of the
// cast `agentEventSchema` collapses back to `AgentEvent`, which is what
// defeated the previous version of this guard. We compare only the `type`
// literals: full-shape comparison trips on readonly-vs-mutable nested fields
// (the reason `agentEventSchema` needs its cast). A missing variant makes
// `_MissingEventType` non-`never`, so the assignment below fails to
// type-check until the schema is brought back in sync. (Inner-field drift —
// e.g. a shortened enum — is caught by the round-trip tests in
// `test/ws-readiness-event.test.ts` and friends.)
type _MissingEventType = Exclude<
  AgentEvent["type"],
  z.infer<typeof agentEventSchemaRaw>["type"]
>;
const _noMissingEventTypes: [_MissingEventType] extends [never] ? true : false =
  true;
void _noMissingEventTypes;

const serverControlSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("session_attached"),
    sessionId: z.string(),
    lastSeq: z.number().int(),
    // Capability flag: the server acks client frames carrying a clientMsgId.
    // Gates the outbox's held-until-acked semantics — absent on old servers,
    // where the client must keep the legacy delivered-on-OPEN behaviour.
    acks: z.literal(true).optional(),
    // Capability flag: this deployment SERVES the design sketch. Gates every
    // "Show the sketch" affordance — the route is registered only where the
    // preview flag, the design store and the account resolver are all wired, and
    // without this the chip fetched a 404 and told the user the layout was never
    // kept, which is a sentence about the DATA that a missing ROUTE cannot
    // support. Absent on an older server ⇒ the client keeps it off.
    figmaPreview: z.literal(true).optional(),
  }),
  z.object({ kind: z.literal("session_expired"), sessionId: z.string() }),
  // Terminal access refusal — the session is fine, this IDENTITY is refused.
  // Unlike `session_expired`, starting a new session is not a recovery. Sent
  // alongside close code 4403; the client latches either signal (see
  // WS_CLOSE_DENIED) so a frame lost in the close race still stops the loop.
  z.object({
    kind: z.literal("session_denied"),
    sessionId: z.string(),
    code: z.string(),
  }),
  z.object({
    kind: z.literal("unknown_intent"),
    sessionId: z.string(),
    name: z.string(),
  }),
  // Receipt ack for a client frame that carried a clientMsgId. THE delivery
  // signal: an outbox entry is released only when this arrives — a send into
  // a dead-but-OPEN socket (the Cloud Run kill mode) never gets one and is
  // re-sent on reconnect, where the server's ingress dedupe drops the
  // duplicate and re-acks. `seq` is the bus seq of the user_message echo
  // (absent for structured frames) — RefineFlow keys its flush suppression
  // on it.
  z.object({
    kind: z.literal("ack"),
    sessionId: z.string(),
    clientMsgId: z.string(),
    seq: z.number().int().optional(),
  }),
  // Server keepalive (see HEARTBEAT_INTERVAL_MS in apps/server/.../stream.ts).
  // Carries no UI payload; receiving it just proves the socket is alive.
  z.object({ kind: z.literal("heartbeat"), sessionId: z.string() }),
]);

export const serverFrameSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("event"), payload: agentEventSchema }),
  z.object({ kind: z.literal("control"), payload: serverControlSchema }),
]);

export type ServerFrame = z.infer<typeof serverFrameSchema>;
export type ServerControl = z.infer<typeof serverControlSchema>;

export interface UserMessageFrame {
  readonly kind: "user_message";
  readonly payload: {
    readonly text: string;
    readonly systemPrompt?: string;
    readonly clientMsgId?: string;
    /**
     * Flow identity (Phase 1 — flow boundaries). A turn from a flow surface
     * (refine / create / implement) carries the issue it belongs to; the seed
     * turn additionally sets `boundary` to open/switch the active flow
     * server-side (fresh CLI conversation + evidence eviction horizon).
     * Subsequent same-flow turns carry only `issueIid` so the server keeps
     * scoping their evidence to this issue. Plain chat turns omit `flow`.
     */
    readonly flow?: {
      readonly issueIid: number;
      readonly boundary?: boolean;
      /**
       * The issue's own title, display metadata for the discovery list. The
       * server stores it as the row's label so an earlier conversation reads
       * "#82 · <Issue-Titel>" instead of a machine seed; it is never evidence,
       * never reaches the model, and the server bounds and sanitizes it before
       * it is written.
       */
      readonly issueTitle?: string;
    };
    /**
     * The byte-exact subset of `text` the user actually typed or picked.
     *
     * A MACHINE-COMPOSED turn (a wizard seed) is mostly not the user's words:
     * an AI-derived issue title, project-map paths, boilerplate. Letting the
     * grounding gate quote THAT is how model prose completes a round trip and
     * comes back as certified user evidence. So a composed turn declares its own
     * user-authored span and the gate scans only that.
     *
     * Presence IS the synthetic marker: every human turn omits it, and the
     * server then scans the whole `text` (which is also what keeps a deploy skew
     * safe). `""` is meaningful and must survive the wire — it says "nothing in
     * this turn is quotable", which is exactly the refine seed. The server
     * re-checks containment rather than trusting it, so a span this client never
     * rendered can only SHRINK the evidence, never widen it.
     */
    readonly evidenceText?: string;
    /**
     * Explicit "New chat" (Phase 1 — flow boundaries) for a pure-chat surface
     * with no issue identity: the server starts a fresh CLI conversation. On a
     * flow surface, `flow: { issueIid, boundary: true }` is used instead.
     */
    readonly newChat?: boolean;
  };
}
export interface PingFrame {
  readonly kind: "ping";
}
/** Accept an all-green readiness card. The server applies it deterministically
 *  (reconstructs the write from the persisted card + re-verifies the token) and
 *  replies with a `readiness_apply_result` event — no agent involved. */
export interface ReadinessAcceptFrame {
  readonly kind: "readiness_accept";
  readonly payload: {
    readonly issueIid: number;
    readonly acceptToken: string;
    readonly clientMsgId?: string;
  };
}
/** Edit a field on the final "ready to apply" preview. Widget fields (priority /
 *  milestone) resolve server-side in one round-trip; free-text fields record a
 *  pending edit and drive a directed agent re-propose. `cancel: true` (no `text`)
 *  withdraws a pending edit ("Keep the previous value"). */
export interface ReadinessFieldEditFrame {
  readonly kind: "readiness_field_edit";
  readonly payload: {
    readonly issueIid: number;
    readonly field: string;
    readonly text?: string;
    readonly cancel?: true;
    readonly clientMsgId?: string;
  };
}
/**
 * SKIP an optional field on the readiness card.
 *
 * A skip used to be mount-local browser state that sent nothing at all, so the
 * agent never learned, the persisted card kept the slot at `needs_input`
 * forever, and the mask died on any remount — the question came straight back.
 * Contrast `declinedAskIds` on `question_answer`, which has ridden to the
 * server since day one precisely because a declined ask must reach the agent or
 * it keeps waiting. NOT a grounding turn: the server emits no echo, and the
 * skip can never become a value.
 */
export interface ReadinessFieldDeclineFrame {
  readonly kind: "readiness_field_decline";
  readonly payload: {
    readonly issueIid: number;
    readonly field: string;
    readonly clientMsgId?: string;
  };
}
/** Request the end-of-refine implementation simulation. The server auto-prompts
 *  the repo-aware sandbox agent for an implementation plan; the result surfaces
 *  as a `readiness_plan_ready` event (or `readiness_plan_unavailable` when no
 *  sandbox is live). `correction` carries the user's clarification when the
 *  business summary didn't match on a prior pass. */
export interface ReadinessPlanRequestFrame {
  readonly kind: "readiness_plan_request";
  readonly payload: {
    readonly issueIid: number;
    readonly correction?: string;
    readonly clientMsgId?: string;
  };
}
/**
 * "Yes, that matches" on the end-of-refine business summary.
 *
 * This is the ONLY user decision behind the implementation plan: `planMarkdown`
 * is server-side by design (it carries repo paths a business owner is
 * deliberately never shown), so the summary is the whole of what the user
 * validated. Apply appends the `## Implementation details` section only for a
 * plan the server has recorded as confirmed — without this frame nothing ever
 * confirms one, and the section is omitted.
 *
 * `planRevision` is REQUIRED and is the plan's persisted `updatedAtMs` as echoed
 * on the `readiness_plan_ready` this screen rendered. It binds the confirm to
 * the exact text the user read: the tail turn produces plan and sizing together
 * and a newer plan can land while the validate screen is up, which this client
 * drops — without the binding the server would confirm a revision the user never
 * saw. A mismatch does not confirm.
 */
export interface ReadinessPlanConfirmFrame {
  readonly kind: "readiness_plan_confirm";
  readonly payload: {
    readonly issueIid: number;
    readonly planRevision: number;
    readonly clientMsgId?: string;
  };
}
/** "Keep going" on the budget wall: raise this session's spend ceiling by
 *  `addUsd` instead of losing the session. A DELTA, not an absolute — the
 *  server adds it to the ceiling the sandbox actually reported, so the client
 *  never names a limit and a replayed frame cannot set an arbitrary one. */
export interface BudgetExtendFrame {
  readonly kind: "budget_extend";
  readonly payload: {
    readonly addUsd: number;
    readonly clientMsgId?: string;
  };
}
/** Request the epic/sub-issue breakdown proposal (after the sizing step
 *  recommended a split, or `force: true` when the user asked for one anyway).
 *  Results surface as `readiness_breakdown_ready` / `_unavailable` events. */
export interface ReadinessBreakdownRequestFrame {
  readonly kind: "readiness_breakdown_request";
  readonly payload: {
    readonly issueIid: number;
    readonly force?: boolean;
    readonly clientMsgId?: string;
  };
}
/**
 * A TYPED adjustment to a proposed split — the lane that replaces the
 * natural-language "Breakdown: …" turn for everything a person decides
 * deterministically (rename, remove, merge, move a must-have, add a piece,
 * and — the case prose could not express at all — retire a must-have).
 *
 * `baseChildrenHash` is a compare-and-swap: the hash of the card the user
 * actually acted on. A mismatch means a newer proposal landed in between, so
 * the server refuses and re-emits rather than applying the op to a different
 * partition.
 *
 * `groundingText` is REQUIRED when the op retires or restores a must-have: it
 * is the canonical must-have list AFTER the change, which the server echoes as
 * a user turn before rewriting the verified green. Re-derived server-side, so
 * it can only shrink the value, never widen it.
 */
export interface ReadinessBreakdownOpFrame {
  readonly kind: "readiness_breakdown_op";
  readonly payload: {
    readonly issueIid: number;
    readonly baseChildrenHash: string;
    readonly op: BreakdownOpPayload;
    readonly groundingText?: string;
    readonly clientMsgId?: string;
  };
}

/** The op shapes the client sends. Structural mirror of `clientOpSchema` in
 *  `@loom/shared/readiness`, which is what the server validates against.
 *
 *  `edit_scope` is deliberately ABSENT — it is a MODEL-only op. A scope line is
 *  the piece's half of the split's prose and the split-level `rationale` is the
 *  other half, so the one op that rewrites a description has to arrive through
 *  `propose_breakdown_ops`, which re-authors the rationale in the same call.
 *  This type declared it long after the server union stopped accepting it; the
 *  frame would have been refused `bad_op`. The client's way to re-word a scope
 *  line is `rewrite_scope`, which routes to the model with that one child
 *  released. */
export type BreakdownOpPayload =
  | { readonly op: "rename"; readonly childId: string; readonly title: string }
  | {
      readonly op: "merge";
      readonly fromChildId: string;
      readonly intoChildId: string;
    }
  | {
      readonly op: "move_criteria";
      readonly criterionIds: ReadonlyArray<string>;
      readonly toChildId: string;
    }
  | {
      readonly op: "remove";
      readonly childId: string;
      readonly disposition: ReadonlyArray<{
        readonly criterionId: string;
        readonly to:
          | { readonly kind: "child"; readonly childId: string }
          | { readonly kind: "park" };
      }>;
    }
  | {
      readonly op: "add_child";
      readonly title: string;
      readonly description: string;
      readonly criterionIds: ReadonlyArray<string>;
    }
  | { readonly op: "park_criteria"; readonly criterionIds: ReadonlyArray<string> }
  | {
      readonly op: "unpark_criteria";
      readonly parkedTexts: ReadonlyArray<string>;
      readonly toChildId: string;
    }
  | { readonly op: "split_further"; readonly childId: string }
  | { readonly op: "rewrite_scope"; readonly childId: string }
  | { readonly op: "free_text"; readonly instruction: string };

/** Confirm the breakdown: ONE user action — the server applies the parent card
 *  first (idempotent when already consumed), then creates the children + links
 *  + parent epic conversion deterministically (receipts-resume: re-sending the
 *  identical frame after a partial failure completes the remainder). */
export interface ReadinessBreakdownAcceptFrame {
  readonly kind: "readiness_breakdown_accept";
  readonly payload: {
    readonly issueIid: number;
    readonly acceptToken: string;
    readonly breakdownToken: string;
    readonly clientMsgId?: string;
  };
}
/**
 * Answer (and/or decline) typed pre-card asks — the `question_pending` channel
 * the agent uses before a readiness card exists.
 *
 * `text` is the ONE grounding turn: byte-identical to what the plain
 * `user_message` path would have sent (a single answer verbatim, several joined
 * by a blank line), so the server's quote-scan behaves exactly as it does for a
 * card answer. `answers` is ATTRIBUTION ONLY — which ask each fragment settles —
 * and is never re-serialised back into `text`; re-composing from it would drift
 * the bytes the gate quotes against. `declinedAskIds` closes an optional ask the
 * user skipped, so the agent stops waiting on it.
 *
 * `issueIid` rides the payload (not just the outbox scope), so a redelivery
 * after a reconnect can only ever land on the flow it was authored in.
 */
export interface QuestionAnswerFrame {
  readonly kind: "question_answer";
  readonly payload: {
    readonly issueIid: number;
    readonly text: string;
    readonly answers: ReadonlyArray<{
      readonly askId: string;
      readonly field: string | null;
      readonly text: string;
      /**
       * Which SHAPE of ask this settles — carried from the render, not inferred
       * server-side. The server's own `pendingAsks` mirror is legitimately empty
       * after an instance recycle, and a concern answer that falls back to the
       * plain-question framing is told to the model as "you now have what you
       * need for <field> — propose it". The reply may be "no, keep it as it is":
       * the model proposes THAT as the value, it grounds (it is a real user
       * turn), and the monotonic merge writes it over the user's own green. The
       * client rendered the concern, so the client knows the kind.
       */
      readonly kind?: "question" | "concern";
    }>;
    readonly declinedAskIds?: ReadonlyArray<string>;
    readonly clientMsgId?: string;
  };
}
/** Resolve a halted autonomous-implement run when the issue failed the DoR
 *  gate. `proceed` tells the server to send the implement seed anyway; the
 *  client handles "refine instead" purely by navigating away. */
export interface ImplementDecisionFrame {
  readonly kind: "implement_decision";
  readonly payload: {
    readonly decision: "proceed";
    readonly clientMsgId?: string;
  };
}
/**
 * "Später" on a Figma connect prompt.
 *
 * A decline MUST reach the server. Client replay is intentionally full, not
 * seq-resume — the client wipes its timeline on reconnect and rebuilds from the
 * bus — so a decline kept in component state resurfaces as a fresh connect
 * prompt on every reconnect, on a chip the user already dismissed. The server
 * answers with a `figma_link_state` row carrying `state: "declined"`, which
 * rides the replay log and comes back for free. No new table.
 *
 * `scope: "session"` is the SECOND decline ("nicht mehr fragen") — it silences
 * every link in this session, present and future, and there is no third ask.
 * It comes back as `declineScope: "session"` on the row.
 *
 * No `sessionId` on the payload: the socket IS the session, exactly as for
 * every other frame here.
 */
export interface FigmaConnectDeclinedFrame {
  readonly kind: "figma_connect_declined";
  readonly payload: {
    readonly linkId: string;
    readonly scope: "link" | "session";
    readonly clientMsgId?: string;
  };
}
/**
 * "Diese Datei erlauben" — the answer to a `not_allowed` chip. The server
 * appends the file key to the project's `.loom.yml` `figma.files` allowlist.
 *
 * The client sends a `linkId` and CANNOT send a file key: it has never been
 * told one (a Figma file key is content, so only the hash rides the wire). The
 * server re-derives the key from its own session-scoped link registry, which is
 * what keeps this from being a door to allowlisting an arbitrary file.
 */
export interface FigmaAllowFileFrame {
  readonly kind: "figma_allow_file";
  readonly payload: {
    readonly linkId: string;
    readonly clientMsgId?: string;
  };
}
/**
 * "Is this the screen you meant?" — yes or no, and nothing else.
 *
 * THERE IS NO `text` FIELD ON THIS FRAME AND IT MUST NEVER GAIN ONE. An
 * approval sentence riding `question_answer` would be echoed onto the bus
 * verbatim as a `user_message`, which makes it matchable by the readiness
 * gate's substring quote scan — a scan with no word boundaries. Approving a
 * sketch means "Loom read the screen I meant" and is evidence for NOTHING: not
 * a role, not an acceptance criterion, not a definition of done, not a point
 * estimate. `provenanceSchema` stays the closed `user | code` union and no
 * `FieldState`, `SlotVerdict` or `greenAtSeq` is written on either verdict.
 *
 * `previewId` is the opaque handle the client was HANDED (the ask's own
 * `subject.previewId`). Like `figma_allow_file`, the client cannot name a file
 * key — it has never been told one — which is what keeps this from being a door
 * to reading someone else's design.
 *
 * A `wrong` verdict writes one content-free registry notice, so the next model
 * turn learns the design is not the screen the user meant. No `user_message`,
 * no bytes.
 */
export interface FigmaPreviewVerdictFrame {
  readonly kind: "figma_preview_verdict";
  readonly payload: {
    readonly askId: string;
    readonly previewId: string;
    readonly verdict: "right" | "wrong";
    readonly clientMsgId?: string;
  };
}
export type ClientFrame =
  | UserMessageFrame
  | PingFrame
  | ReadinessAcceptFrame
  | ReadinessFieldEditFrame
  | ReadinessFieldDeclineFrame
  | ReadinessPlanRequestFrame
  | ReadinessPlanConfirmFrame
  | BudgetExtendFrame
  | ReadinessBreakdownRequestFrame
  | ReadinessBreakdownOpFrame
  | ReadinessBreakdownAcceptFrame
  | QuestionAnswerFrame
  | ImplementDecisionFrame
  | FigmaConnectDeclinedFrame
  | FigmaAllowFileFrame
  | FigmaPreviewVerdictFrame;

export interface WsClientHandlers {
  readonly onEvent: (event: AgentEvent) => void;
  readonly onControl: (control: ServerControl) => void;
  readonly onOpen?: () => void;
  readonly onClose?: (info: { code: number; reason: string }) => void;
  readonly onProtocolError?: (err: Error) => void;
}

export interface WsClient {
  /** Returns true if the frame was handed to an OPEN socket, false if dropped. */
  send(frame: ClientFrame): boolean;
  close(): void;
}

/**
 * If no frame (event, control, or heartbeat) arrives within this window the
 * socket is treated as dead-but-OPEN — a silent proxy drop where `readyState`
 * still reads OPEN so `send()` would post into the void. Must be a comfortable
 * multiple of the server's HEARTBEAT_INTERVAL_MS (25s) so a healthy but quiet
 * connection never trips it. ~75s = 3 missed heartbeats.
 */
const LIVENESS_TIMEOUT_MS = 75_000;

/**
 * Server close code for a TERMINAL access refusal (approval revoked, session
 * URL opened by another user). Paired with a `session_denied` control frame,
 * but the code is the load-bearing half: it is delivered by the close event
 * itself, so it survives the frame being lost in the send-then-close race.
 * Reconnecting after this can only fail identically.
 */
export const WS_CLOSE_DENIED = 4403;

/**
 * Server close code for a TRANSIENT access-check failure — the approval store
 * is unreachable. Explicitly NOT terminal: reconnecting is how an open tab
 * rides out the outage, so this stays on the normal backoff path.
 */
export const WS_CLOSE_ACCESS_UNAVAILABLE = 4503;

export const wsUrlFor = (sessionId: string): string => {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/sessions/${encodeURIComponent(sessionId)}/stream`;
};

/** Our private close code for "the browser says it has no network". */
export const WS_CLOSE_BROWSER_OFFLINE = 4409;

export const openWsSession = (sessionId: string, handlers: WsClientHandlers): WsClient => {
  const socket = new WebSocket(wsUrlFor(sessionId));

  // A socket is reported closed ONCE, at the first moment the client knows it
  // is dead — not when the browser gets round to firing `close`. On a network
  // that is gone, `close()` starts a closing handshake that cannot finish, and
  // Chrome fires `close` only when it times out, 60 s later (dev #280/#283:
  // liveness at 15:24:31, `socket_close` 1006 at 15:25:31). All that minute the
  // page read `open`: it said "thinking…" over a turn that had ended, and its
  // "Connection to Loom lost" line never came. The later `close` event is then
  // a diag line only — reporting it again would tear down the socket the
  // consumer has already reconnected on.
  let closeReported = false;
  const reportClose = (info: { code: number; reason: string }): void => {
    if (closeReported) return;
    closeReported = true;
    clearLiveness();
    detachOffline();
    handlers.onClose?.(info);
  };
  const forceClose = (code: number, reason: string): void => {
    try {
      socket.close(code, reason);
    } catch {
      /* already closing */
    }
    reportClose({ code, reason });
  };

  // Liveness watchdog: any inbound frame (re)arms it; if it fires, the socket
  // has gone silent past the server's heartbeat cadence, so it is dead: close
  // it and tell the consumer at once, which reconnects instead of believing a
  // dead socket is still OPEN and sending messages into the void.
  let livenessTimer: ReturnType<typeof setTimeout> | null = null;
  function clearLiveness(): void {
    if (livenessTimer !== null) {
      clearTimeout(livenessTimer);
      livenessTimer = null;
    }
  }
  const armLiveness = (): void => {
    clearLiveness();
    livenessTimer = setTimeout(() => {
      // 4408 = our private "heartbeat_timeout"; surfaced via onClose.
      wsDiag("liveness_fire", { sessionId, afterMs: LIVENESS_TIMEOUT_MS });
      forceClose(4408, "heartbeat_timeout");
    }, LIVENESS_TIMEOUT_MS);
  };

  // The browser saying it has NO network is a first-hand fact: no frame can
  // arrive on this socket, and waiting 75 s for the watchdog to agree is 75 s
  // of a page claiming Loom is working. (A localhost socket survives it; the
  // reconnect that follows simply lands.)
  const onBrowserOffline = (): void => {
    wsDiag("browser_offline", { sessionId });
    forceClose(WS_CLOSE_BROWSER_OFFLINE, "browser_offline");
  };
  const canListen = typeof window !== "undefined" && typeof window.addEventListener === "function";
  if (canListen) window.addEventListener("offline", onBrowserOffline);
  function detachOffline(): void {
    if (canListen) window.removeEventListener("offline", onBrowserOffline);
  }

  socket.addEventListener("open", () => {
    if (closeReported) return;
    wsDiag("socket_open", { sessionId });
    armLiveness();
    handlers.onOpen?.();
  });
  socket.addEventListener("close", (ev) => {
    // The single most important lost-session signal: code 1006 = Cloud Run
    // killed the socket; 4408 = our own watchdog fired; 1000/1001 = clean.
    wsDiag("socket_close", {
      sessionId,
      code: ev.code,
      reason: ev.reason,
      wasClean: ev.wasClean,
      alreadyReported: closeReported,
    });
    reportClose({ code: ev.code, reason: ev.reason });
  });
  socket.addEventListener("message", (ev) => {
    // A socket already reported dead is done: the consumer is on its next one.
    if (closeReported) return;
    armLiveness(); // any frame — including a heartbeat — proves the socket is alive
    let parsed: unknown;
    try {
      parsed = JSON.parse(typeof ev.data === "string" ? ev.data : ev.data.toString());
    } catch (err) {
      handlers.onProtocolError?.(err as Error);
      return;
    }
    const result = serverFrameSchema.safeParse(parsed);
    if (!result.success) {
      handlers.onProtocolError?.(new Error(result.error.message));
      return;
    }
    if (result.data.kind === "event") {
      const t = result.data.payload.type;
      // assistant_delta is high-frequency noise; record it quietly so the
      // ring buffer still shows streaming activity without flooding console.
      wsDiag(`event:${t}`, { seq: result.data.payload.seq }, t === "assistant_delta");
      handlers.onEvent(result.data.payload);
    } else {
      const c = result.data.payload;
      // Heartbeats are frequent but their CADENCE is the diagnostic: a gap in
      // the `dt` deltas before a 1006 close shows the server stopped sending.
      wsDiag(`control:${c.kind}`, { sessionId }, c.kind === "heartbeat");
      handlers.onControl(c);
    }
  });
  return {
    send: (frame) => {
      const open = socket.readyState === WebSocket.OPEN;
      wsDiag("send", { sessionId, kind: frame.kind, delivered: open });
      if (open) {
        socket.send(JSON.stringify(frame));
        return true;
      }
      return false;
    },
    close: () => {
      wsDiag("socket_close_requested", { sessionId });
      clearLiveness();
      detachOffline();
      socket.close();
    },
  };
};
